import type { ReactNode } from "react"
import { useId, useState } from "react"

import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import { useDesktopViewport } from "../../BatteryOnlyProposal/use-desktop-viewport"
import { formatUsdExact } from "../../BatteryOnlyProposal/view-helpers"

/**
 * The investment card every proposal kind presents: a gradient slot holding
 * the stored ledger beside its incentives. These pieces format the figures the
 * server returned and own no requests or arithmetic; each kind decides which
 * rows it has and what else the card carries.
 */

const MINUS = `−`

/** A positive amount that comes off the figure above it. */
export const reduction = (amount: number): string =>
  `${MINUS}${formatUsdExact(amount)}`

/** A positive amount that adds to the figure above it. */
export const charge = (amount: number): string => `+${formatUsdExact(amount)}`

/** A label and a positive whole amount; the row kind gives it its sign. */
export type SummaryAmountLine = { label: string; amount: number }

export function SummarySlot({
  children,
  className,
  testId,
}: {
  children: ReactNode
  className?: string
  testId?: string
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      className={clsx(
        `relative mt-3.5 overflow-hidden rounded-lg bg-[image:var(--bp-grad-accent)] px-6 py-8 text-white lg:px-12 lg:py-10`,
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_20%,rgba(255,255,255,0.18),transparent_50%)]"
      />
      {children}
    </div>
  )
}

export function CardEyebrow({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div
      className={clsx(
        // Gold rather than white: a translucent white label disappears into
        // the accent gradient behind it, and every figure on this card is
        // already white.
        `text-[length:var(--step--2)] font-bold tracking-[0.18em] text-bp-gold uppercase`,
        className,
      )}
    >
      {children}
    </div>
  )
}

export function FooterLabel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-[length:var(--step--2)] tracking-[0.06em] text-white/85 uppercase">
      {children}
    </div>
  )
}

/** One Financial Breakdown row, already formatted. */
export type LedgerLine = {
  label: string
  value: string
  badge?: string
  emphasis?: boolean
}

export function LedgerRow({
  label,
  value,
  badge,
  emphasis = false,
  last = false,
}: {
  label: string
  value: string
  badge?: string
  emphasis?: boolean
  last?: boolean
}): JSX.Element {
  return (
    <div
      data-testid="financial-summary-row"
      className={clsx(
        `flex items-baseline justify-between gap-6 py-3`,
        !last && `border-b border-white/15`,
      )}
    >
      <span
        className={clsx(
          `flex items-center gap-2.5 text-[length:var(--step--1)] text-white/95`,
          emphasis && `font-semibold`,
        )}
      >
        {label}
        {badge ? (
          <span className="rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-extrabold tracking-[0.08em] text-bp-deduction">
            {badge}
          </span>
        ) : null}
      </span>
      <span className="font-display text-[length:var(--step-0)] font-semibold tracking-[-0.01em] whitespace-nowrap tabular-nums">
        {value}
      </span>
    </div>
  )
}

/**
 * The card's title: the "Investment breakdown" eyebrow over "Your financial
 * summary.", with an optional subtitle (Battery's financing terms) beneath.
 */
export function SummaryHeading({
  children = null,
}: {
  children?: ReactNode
}): JSX.Element {
  return (
    <>
      <CardEyebrow className="mb-2">Investment breakdown</CardEyebrow>
      <div className="mb-6">
        <h3 className="font-display text-[length:var(--step-3)] leading-[1.05] font-semibold tracking-[-0.025em] text-white">
          Your financial summary.
        </h3>
        {children}
      </div>
    </>
  )
}

/** The Financial Breakdown eyebrow and its rows, in the order given. */
export function LedgerRows({
  lines,
}: {
  lines: readonly LedgerLine[]
}): JSX.Element {
  return (
    <>
      {/* Names the rows so anything above them reads as a separate statement
          rather than the head of this list. */}
      <CardEyebrow className="mb-3">Financial Breakdown</CardEyebrow>

      {lines.map((line, index) => (
        <LedgerRow
          key={`${line.label}-${index}`}
          label={line.label}
          badge={line.badge}
          emphasis={line.emphasis}
          value={line.value}
          last={index === lines.length - 1}
        />
      ))}
    </>
  )
}

/**
 * The ledger's closing row: Net system cost, with room beside it for a
 * payment column.
 */
export function NetSystemCostFooter({
  netSystemCost,
  children = null,
}: {
  netSystemCost: number
  children?: ReactNode
}): JSX.Element {
  return (
    <LedgerFooter
      label="Net system cost"
      amount={netSystemCost}
      testId="financial-summary-net-system-cost"
    >
      {children}
    </LedgerFooter>
  )
}

/** The ledger's closing figure, with room beside it for a payment column. */
export function LedgerFooter({
  label,
  amount,
  testId,
  children = null,
}: {
  label: string
  amount: number
  testId: string
  children?: ReactNode
}): JSX.Element {
  return (
    // Top-aligned so Net system cost and any payment beside it start their
    // labels on one line: the two figures are different sizes, so bottom
    // alignment staggered the labels.
    <div
      data-testid="financial-summary-footer"
      className="mt-2 flex flex-wrap items-start justify-between gap-x-6 gap-y-5 border-t-2 border-white/35 pt-5"
    >
      <div>
        <FooterLabel>{label}</FooterLabel>
        <div
          data-testid={testId}
          className="font-display mt-1.5 text-[length:var(--step-4)] leading-none font-bold tracking-[-0.025em] tabular-nums"
        >
          {formatUsdExact(amount)}
        </div>
      </div>
      {children}
    </div>
  )
}

export function IncentiveLines({
  lines,
}: {
  lines: readonly SummaryAmountLine[]
}): JSX.Element {
  return (
    <ul className="m-0 list-none p-0">
      {lines.map((incentive, index) => (
        <li
          key={`${incentive.label}-${index}`}
          className="flex items-baseline justify-between gap-6 border-b border-white/20 py-3 first:pt-0"
        >
          <span className="text-[length:var(--step--1)] text-white/95">
            {incentive.label}
          </span>
          <span className="font-display text-[length:var(--step-0)] font-semibold tracking-[-0.01em] whitespace-nowrap tabular-nums">
            {formatUsdExact(incentive.amount)}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function TotalIncentivesRow({
  total,
  testId = `total-incentives`,
}: {
  total: number
  testId?: string
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="flex items-baseline justify-between gap-6 pt-3.5"
    >
      <span className="font-display text-[length:var(--step-0)] font-semibold tracking-[-0.015em]">
        Total Incentives
      </span>
      <span className="font-display text-[length:var(--step-1)] font-bold tracking-[-0.02em] whitespace-nowrap tabular-nums">
        {formatUsdExact(total)}
      </span>
    </div>
  )
}

function IncentivesAccordion({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const regionId = useId()

  return (
    <div className="border-t border-white/25 pt-6">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? regionId : undefined}
        onClick={() => setExpanded((open) => !open)}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <CardEyebrow>Incentives</CardEyebrow>
        <ChevronDownIcon
          aria-hidden="true"
          className={clsx(
            `size-5 shrink-0 transition-transform`,
            expanded && `rotate-180`,
          )}
        />
      </button>
      {expanded ? (
        <div id={regionId} className="mt-4">
          {children}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The card's frame: an optional header, the ledger column, and — when there is
 * one — the Incentives column on desktop or an Incentives accordion below.
 */
export function SummaryCardLayout({
  ledger,
  incentives,
  header = null,
  className,
  testId,
  incentivesTestId = `financial-summary-incentives`,
}: {
  ledger: ReactNode
  /** The Incentives detail, or null when the card has no Incentives column. */
  incentives: ReactNode
  header?: ReactNode
  className?: string
  testId?: string
  incentivesTestId?: string
}): JSX.Element {
  const isDesktop = useDesktopViewport()
  const hasIncentives = incentives != null

  return (
    <SummarySlot testId={testId} className={className}>
      {header}
      {/* The ledger keeps its half-width column even without incentives so
          the card reads the same across proposals. */}
      <div className="relative grid gap-8 lg:grid-cols-2 lg:gap-12">
        <div>{ledger}</div>
        {hasIncentives && isDesktop ? (
          <div
            data-testid={incentivesTestId}
            className="border-l border-white/25 pl-12"
          >
            <CardEyebrow className="mb-4">Incentives</CardEyebrow>
            {incentives}
          </div>
        ) : hasIncentives ? (
          <IncentivesAccordion>{incentives}</IncentivesAccordion>
        ) : null}
      </div>
    </SummarySlot>
  )
}
